import React, { useState, useEffect } from 'react';
import { Key, Eye, EyeOff, Save, Trash2, Lock, Unlock, ExternalLink, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { addGlobalLog } from '@/components/GlobalConsole';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SourcesConfigPanel = ({ token, isOpen, onClose }) => {
  const [sources, setSources] = useState({ free_sources: [], premium_sources: [], by_category: {} });
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    if (isOpen && token) {
      fetchSources();
    }
  }, [isOpen, token]);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/sources/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSources(response.data);
      addGlobalLog('data', 'SOURCES', `${response.data.total_free} gratuites, ${response.data.total_premium} premium`);
    } catch (err) {
      toast.error('Erreur chargement des sources');
    } finally {
      setLoading(false);
    }
  };

  const saveCredential = async (sourceId) => {
    const cred = credentials[sourceId];
    if (!cred?.username || !cred?.password) {
      toast.error('Identifiant et mot de passe requis');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/sources/credentials`,
        { [sourceId]: cred },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Credentials sauvegardés pour ${sourceId}`);
      addGlobalLog('success', 'SOURCES', `Credentials ${sourceId} sauvegardés`);
      fetchSources();
    } catch (err) {
      toast.error('Erreur sauvegarde');
    }
  };

  const deleteCredential = async (sourceId) => {
    try {
      await axios.delete(
        `${API_URL}/api/sources/credentials/${sourceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Credentials supprimés pour ${sourceId}`);
      setCredentials(prev => {
        const newCreds = { ...prev };
        delete newCreds[sourceId];
        return newCreds;
      });
      fetchSources();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const categories = [
    { id: 'all', name: 'Toutes', count: sources.total_free + sources.total_premium },
    { id: 'energy', name: 'Énergie', icon: '⚡' },
    { id: 'geopolitics', name: 'Géopolitique', icon: '🌍' },
    { id: 'military', name: 'Défense', icon: '🎖️' },
    { id: 'maritime', name: 'Maritime', icon: '🚢' },
  ];

  const getFilteredSources = () => {
    if (activeCategory === 'all') {
      return [...sources.free_sources, ...sources.premium_sources];
    }
    const catData = sources.by_category?.[activeCategory];
    if (!catData) return [];
    return [...(catData.free || []), ...(catData.premium || [])];
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-marine-navy border-2 border-marine-cyan/50 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-marine-cyan/30 bg-black/50">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6 text-marine-cyan" />
              <h2 className="font-heading text-marine-cyan text-xl tracking-wider">
                CONFIGURATION SOURCES MÉDIA
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={fetchSources}
                disabled={loading}
                className="p-2 text-marine-cyan hover:bg-marine-cyan/20 rounded"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="px-6 py-3 bg-black/30 border-b border-marine-slate/30 flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Unlock className="w-4 h-4 text-marine-green" />
              <span className="font-mono text-marine-green text-sm">{sources.total_free} sources gratuites</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-marine-amber" />
              <span className="font-mono text-marine-amber text-sm">{sources.total_premium} sources premium</span>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="px-6 py-3 border-b border-marine-slate/30 flex gap-2 overflow-x-auto">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded font-mono text-sm whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-marine-cyan text-black font-bold'
                    : 'bg-black/50 text-white/90 hover:bg-marine-cyan/20'
                }`}
              >
                {cat.icon && <span className="mr-2">{cat.icon}</span>}
                {cat.name}
                {cat.count !== undefined && <span className="ml-2 opacity-70">({cat.count})</span>}
              </button>
            ))}
          </div>

          {/* Sources List */}
          <div className="overflow-y-auto max-h-[50vh] p-4 space-y-2">
            {loading ? (
              <div className="text-center py-12 text-white/80">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                Chargement des sources...
              </div>
            ) : (
              getFilteredSources().map(source => (
                <div
                  key={source.id}
                  className={`p-4 rounded-lg border-2 ${
                    source.type === 'premium'
                      ? 'bg-gradient-to-r from-amber-900/20 to-black border-marine-amber/30'
                      : 'bg-black/50 border-marine-slate/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {source.type === 'premium' ? (
                        <Lock className="w-5 h-5 text-marine-amber" />
                      ) : (
                        <Unlock className="w-5 h-5 text-marine-green" />
                      )}
                      <div>
                        <p className="font-mono text-white font-bold">{source.name}</p>
                        <p className="font-mono text-white/80 text-xs">
                          {source.category} | Poids: {source.weight || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {source.enabled && (
                        <span className="px-2 py-1 bg-marine-green/20 text-marine-green text-xs font-mono rounded">
                          ACTIF
                        </span>
                      )}
                      {source.has_credentials && (
                        <span className="px-2 py-1 bg-marine-cyan/20 text-marine-cyan text-xs font-mono rounded">
                          CONFIGURÉ
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Premium source credentials form */}
                  {source.type === 'premium' && (
                    <div className="mt-4 pt-4 border-t border-marine-slate/30">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block font-mono text-white/80 text-xs mb-1">IDENTIFIANT</label>
                          <input
                            type="text"
                            value={credentials[source.id]?.username || ''}
                            onChange={e => setCredentials(prev => ({
                              ...prev,
                              [source.id]: { ...prev[source.id], username: e.target.value }
                            }))}
                            placeholder="Email ou username"
                            className="w-full px-3 py-2 bg-black border border-marine-slate/50 text-white font-mono text-sm rounded focus:border-marine-cyan outline-none"
                          />
                        </div>
                        <div>
                          <label className="block font-mono text-white/80 text-xs mb-1">MOT DE PASSE</label>
                          <div className="relative">
                            <input
                              type={showPasswords[source.id] ? 'text' : 'password'}
                              value={credentials[source.id]?.password || ''}
                              onChange={e => setCredentials(prev => ({
                                ...prev,
                                [source.id]: { ...prev[source.id], password: e.target.value }
                              }))}
                              placeholder="••••••••"
                              className="w-full px-3 py-2 bg-black border border-marine-slate/50 text-white font-mono text-sm rounded focus:border-marine-cyan outline-none pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPasswords(prev => ({ ...prev, [source.id]: !prev[source.id] }))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white"
                            >
                              {showPasswords[source.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <button
                          onClick={() => saveCredential(source.id)}
                          className="flex items-center gap-2 px-4 py-2 bg-marine-green/20 border border-marine-green text-marine-green hover:bg-marine-green hover:text-black font-mono text-sm rounded transition-all"
                        >
                          <Save className="w-4 h-4" />
                          Sauvegarder
                        </button>
                        {source.has_credentials && (
                          <button
                            onClick={() => deleteCredential(source.id)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500 hover:text-white font-mono text-sm rounded transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        )}
                        <a
                          href={source.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 text-white/80 hover:text-white font-mono text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ouvrir le site
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-marine-slate/30 bg-black/30">
            <p className="font-mono text-white/80 text-xs text-center">
              Les credentials sont chiffrés et stockés de manière sécurisée. 
              Les sources premium nécessitent un abonnement actif auprès du fournisseur.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SourcesConfigPanel;
